import { ApolloQueryResult, OperationVariables } from '@apollo/client'
import {
  ButtonBase,
  IconButton,
  Paper,
  Typography,
  Fade
} from '@material-ui/core'
import LeftArrowIcon from '@material-ui/icons/ChevronLeft'
import RightArrowIcon from '@material-ui/icons/ChevronRight'
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow'
import RefreshIcon from '@material-ui/icons/Refresh'
import moment from 'moment-jalaali'
import { useEffect, useMemo, useRef, useState } from 'react'

import { DateRangePicker } from '../../components/DatePicker/DateRangePicker'
import {
  formatDateFullObject,
  getDatesList,
  addDaysToDate,
  getTodayDate,
  DateObject,
  isWeekend
} from '../../helpers/date'
import { lang } from '../../localization'
import { AllocationLogDetails } from '../../types/inventoryAllocationLogs'
import useStyles from './styles/InventoryDateRow.styles'

interface InventoryDateRowProps {
  selectedDateInDatePicker: DateObject
  setselectedDateInDatePicker: React.Dispatch<React.SetStateAction<DateObject>>
  range?: 'TWO_WEEKS' | 'MONTH'
  allowedRange: DateObject
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<
    ApolloQueryResult<{
      allocationLogDetails: AllocationLogDetails
    }>
  >
}

const InventoryDateRow: React.FC<InventoryDateRowProps> = ({
  selectedDateInDatePicker,
  setselectedDateInDatePicker,
  range = 'TWO_WEEKS',
  allowedRange,
  refetch
}) => {
  const classes = useStyles()

  const perPage = useMemo(() => (range === 'TWO_WEEKS' ? 14 : 31), [range])

  const [isOpen, setisOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef?.current?.contains(event.target as Node) &&
        isOpen
      ) {
        setisOpen(false)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [wrapperRef, isOpen])

  const datesList = useMemo(
    () => getDatesList(perPage, selectedDateInDatePicker.gregorianStartDate),
    [perPage, selectedDateInDatePicker.gregorianStartDate]
  )

  // Helper function to ensure the date is within the allowed range and at least 14 days
  const adjustToAllowedRange = (
    newStartDate: string,
    newEndDate: string
  ): { startDate: string; endDate: string } => {
    const minDate = moment(allowedRange.gregorianStartDate)
    const maxDate = moment(allowedRange.gregorianEndDate)

    let adjustedStartDate = moment(newStartDate)
    let adjustedEndDate = moment(newEndDate)

    const allowedRangeLength = maxDate.diff(minDate, 'days') + 1

    // If the allowed range is less than 14 days
    if (allowedRangeLength < 14) {
      adjustedStartDate = minDate
      adjustedEndDate = minDate.clone().add(allowedRangeLength - 1, 'days')
    } else {
      // Adjust start date if it's before the allowed range
      if (adjustedStartDate.isBefore(minDate)) {
        adjustedStartDate = minDate
        adjustedEndDate = minDate.clone().add(13, 'days')
      }

      // Adjust end date if it's after the allowed range
      if (adjustedEndDate.isAfter(maxDate)) {
        adjustedEndDate = maxDate
        adjustedStartDate = maxDate.clone().subtract(13, 'days')
      }

      // Ensure the range is at least 14 days
      if (adjustedEndDate.diff(adjustedStartDate, 'days') < 13) {
        adjustedEndDate = adjustedStartDate.clone().add(13, 'days')
      }
    }

    // Ensure we don't exceed the maximum date in the allowed range
    if (adjustedEndDate.isAfter(maxDate)) {
      adjustedEndDate = maxDate
    }

    return {
      startDate: adjustedStartDate.format('YYYY-MM-DD'),
      endDate: adjustedEndDate.format('YYYY-MM-DD')
    }
  }

  const handleChangeDate = (newStartDate: string, newEndDate: string) => {
    // Adjust dates to the allowed range
    const { startDate, endDate } = adjustToAllowedRange(
      newStartDate,
      newEndDate
    )

    setselectedDateInDatePicker({
      ...selectedDateInDatePicker,
      gregorianStartDate: startDate,
      gregorianEndDate: endDate
    })
  }

  return (
    <Paper className={classes.paperWeekly} elevation={0}>
      <div className={classes.bigDateContainer}>
        <div>
          <IconButton onClick={() => refetch()}>
            <RefreshIcon />
          </IconButton>
        </div>
        <IconButton
          size="small"
          disabled={
            selectedDateInDatePicker.gregorianStartDate === getTodayDate()
          }
          onClick={() => {
            const newFirstDate = addDaysToDate(
              selectedDateInDatePicker.gregorianStartDate!,
              -perPage
            )
            const today = getTodayDate()
            const newGregorianStartDate =
              newFirstDate > today ? newFirstDate : today
            const newGregorianEndDate = addDaysToDate(newGregorianStartDate, 13)

            handleChangeDate(newGregorianStartDate, newGregorianEndDate)
          }}>
          <DoubleArrowIcon
            className={[classes.prevPageIcon, classes.arrowIcon].join(' ')}
            fontSize="small"
          />
        </IconButton>
        <IconButton
          size="small"
          disabled={
            selectedDateInDatePicker.gregorianStartDate === getTodayDate()
          }
          onClick={() => {
            const newStartDate = addDaysToDate(
              selectedDateInDatePicker.gregorianStartDate!,
              -1
            )
            const newEndDate = addDaysToDate(
              selectedDateInDatePicker.gregorianEndDate!,
              -1
            )

            handleChangeDate(newStartDate, newEndDate)
          }}>
          <LeftArrowIcon fontSize="small" className={classes.arrowIcon} />
        </IconButton>
        {datesList && datesList.length > 0 && (
          <div className={classes.bidDateContainer} ref={wrapperRef}>
            <ButtonBase onClick={() => setisOpen(!isOpen)}>
              <Typography
                variant="h3"
                color="primary"
                className={classes.bigDate}>
                {formatDateFullObject(datesList[0])}
              </Typography>
            </ButtonBase>
            {isOpen && (
              <Fade in={isOpen}>
                <div className={classes.datePickerContainer}>
                  <DateRangePicker
                    onChange={(d: DateObject) => setselectedDateInDatePicker(d)}
                    dateObject={selectedDateInDatePicker}
                    autoSelectedEndDate
                    numberOfHoveredItems={perPage}
                    type={lang === 'fa' ? 'jalaali' : 'gregorian'}
                  />
                </div>
              </Fade>
            )}
          </div>
        )}
        <IconButton
          size="small"
          onClick={() => {
            const newStartDate = addDaysToDate(
              selectedDateInDatePicker.gregorianStartDate!,
              1
            )
            const newEndDate = addDaysToDate(
              selectedDateInDatePicker.gregorianEndDate!,
              1
            )

            handleChangeDate(newStartDate, newEndDate)
          }}>
          <RightArrowIcon fontSize="small" className={classes.arrowIcon} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => {
            const newStartDate = addDaysToDate(
              selectedDateInDatePicker.gregorianStartDate!,
              perPage
            )
            const newEndDate = addDaysToDate(
              selectedDateInDatePicker.gregorianEndDate!,
              perPage
            )

            handleChangeDate(newStartDate, newEndDate)
          }}>
          <DoubleArrowIcon fontSize="small" className={classes.arrowIcon} />
        </IconButton>
      </div>
      {datesList.map((item) => (
        <div
          className={[
            classes.dayContainer,
            isWeekend(item) ? classes.weekendDayContainer : '',
            range === 'MONTH' ? classes.dayContainerSmall : ''
          ].join(' ')}
          key={item.gregorianDate.date}>
          <span>
            {item[lang === 'fa' ? 'jalaaliDate' : 'gregorianDate'].shortMonth}
          </span>
          <Typography variant="h4">
            {item[lang === 'fa' ? 'jalaaliDate' : 'gregorianDate'].monthDay}
          </Typography>
          <span>{item.gregorianDate.weekDay}</span>
        </div>
      ))}
    </Paper>
  )
}

export default InventoryDateRow
